/**
 * Pictures that are NOT a person's real photo (Gordon 2026-10-31: "Still a lot
 * of users with just initials thumbnails, update them with the BB auto text
 * vector thumbnails"). Two kinds turned up in the data:
 *   • initials SVGs the old Super Admin "Auto avatars" tool saved as
 *     profileImageUrl (data:image/svg+xml, flagged generatedAvatar),
 *   • initials PNGs from the avatar builder's old "Initials" tab, and
 *   • Google's default account picture — one flat colour with a white letter —
 *     copied in at sign-up.
 * Both should show the animated BB vector art (GenerativeArtistAvatar) instead.
 */

/** An initials picture we generated ourselves — never a real photo: the admin
 *  tool's SVG data URLs, or the avatar builder's old "Initials" PNG uploads. */
export const isGeneratedInitials = (url?: unknown): boolean =>
  typeof url === 'string' && (url.startsWith('data:image/svg+xml') || /(\/|%2F)avatars(\/|%2F)initials-\d+\.png/i.test(url));

/** The photo to show, or '' when the stored picture is only a generated placeholder. */
export const realPhoto = (url?: string | null): string =>
  url && !isGeneratedInitials(url) ? url : '';

/**
 * Pixel test for Google's default avatar on a small square sample (RGB or RGBA).
 * Uniform coloured border + very few colours = a letter on a flat tile. A white
 * border is a real logo on white (e.g. a black "a"), so that stays a photo.
 */
export function looksLikeLetterAvatar(px: ArrayLike<number>, size: number, channels: 3 | 4): boolean {
  const at = (x: number, y: number) => (y * size + x) * channels;
  const r0 = px[0], g0 = px[1], b0 = px[2];
  let edge = 0, edgeN = 0;
  for (let i = 0; i < size; i++) {
    for (const [x, y] of [[i, 0], [i, size - 1], [0, i], [size - 1, i]]) {
      const k = at(x, y);
      edge += Math.abs(px[k] - r0) + Math.abs(px[k + 1] - g0) + Math.abs(px[k + 2] - b0);
      edgeN++;
    }
  }
  if (edge / edgeN > 6) return false;
  const colours = new Set<number>();
  for (let k = 0; k < size * size * channels; k += channels) colours.add(((px[k] >> 4) << 8) | ((px[k + 1] >> 4) << 4) | (px[k + 2] >> 4));
  if (colours.size === 1) return true; // a blank, single-colour picture
  const luma = 0.2126 * r0 + 0.7152 * g0 + 0.0722 * b0;
  return colours.size <= 40 && luma < 235;
}

/**
 * Browser-only: is this Google profile picture the default letter avatar?
 * Google's photo host allows cross-origin reads, so a 32×32 canvas sample is
 * enough. Resolves false on any doubt (other hosts, errors, timeout) — a real
 * photo must never be dropped.
 */
export function isDefaultLetterAvatar(url: string, timeoutMs = 2000): Promise<boolean> {
  if (typeof window === 'undefined' || !/googleusercontent\.com\//.test(url)) return Promise.resolve(false);
  return new Promise(resolve => {
    const done = (v: boolean) => { clearTimeout(timer); resolve(v); };
    const timer = setTimeout(() => resolve(false), timeoutMs);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = c.height = 32;
        const ctx = c.getContext('2d');
        if (!ctx) return done(false);
        ctx.drawImage(img, 0, 0, 32, 32);
        done(looksLikeLetterAvatar(ctx.getImageData(0, 0, 32, 32).data, 32, 4));
      } catch { done(false); }
    };
    img.onerror = () => done(false);
    img.src = url.replace(/=s\d+(-c)?$/, '=s64-c');
  });
}

const letterChecks = new Map<string, Promise<boolean>>();
/** isDefaultLetterAvatar, checked once per URL per visit. */
export function checkLetterAvatar(url: string): Promise<boolean> {
  let p = letterChecks.get(url);
  if (!p) { p = isDefaultLetterAvatar(url); letterChecks.set(url, p); }
  return p;
}
